import {
  EllipsisHorizontalIcon,
  HashtagIcon,
  PencilSquareIcon,
  PlusIcon,
  StarIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CaseCategory,
  CASE_DRAG_MIME,
  useCaseCategories,
} from "../../context/CaseCategoriesContext";
import Button from "../common/Button";
import Input from "../common/Input";

interface CaseCategoriesProps {
  isMenuCollapsed: boolean;
  onNavigate?: () => void;
}

const MENU_WIDTH = 176; // w-44

// Short label for the collapsed rail: first letter of each of the first two
// words ("Traffic Stops" → "TS"), or the first two letters of a single word
// ("Homicide" → "HO").
const categoryInitials = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

export default function CaseCategories({
  isMenuCollapsed,
  onNavigate,
}: CaseCategoriesProps) {
  const {
    categories,
    favoritesCount,
    activeFilter,
    setActiveFilter,
    createCategory,
    updateCategory,
    deleteCategory,
    addCaseToCategory,
    toggleFavorite,
    isDraggingCase,
  } = useCaseCategories();
  const navigate = useNavigate();
  const location = useLocation();
  const onCasesPage = location.pathname.startsWith("/cases");

  // The category name modal handles both creating and renaming.
  const [modal, setModal] = useState<
    { mode: "create" } | { mode: "rename"; id: string } | null
  >(null);
  const [nameValue, setNameValue] = useState("");
  // Category pending deletion (drives the confirmation modal).
  const [deletingCategory, setDeletingCategory] = useState<CaseCategory | null>(
    null
  );
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [favDragOver, setFavDragOver] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  // Viewport coords for the portal-rendered "⋯" menu (avoids clipping by the
  // sidebar's overflow containers).
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Focus the name field when the modal opens, caret at the end (so renaming
  // doesn't drop the cursor before the existing name).
  useEffect(() => {
    if (modal) {
      const t = setTimeout(() => {
        const input =
          dialogRef.current?.querySelector<HTMLInputElement>("input");
        if (!input) return;
        input.focus();
        const end = input.value.length;
        input.setSelectionRange(end, end);
      }, 50);
      return () => clearTimeout(t);
    }
  }, [modal]);

  // Any mousedown outside the menu/trigger closes it. The trigger and the
  // portal menu stop propagation so interacting with them never self-closes.
  useEffect(() => {
    const handler = () => setMenuOpenId(null);
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const goToFilter = (filter: typeof activeFilter) => {
    setActiveFilter(filter);
    if (!onCasesPage) navigate("/cases");
    onNavigate?.();
  };

  const openMenu = (categoryId: string, trigger: HTMLElement) => {
    const rect = trigger.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      left: Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8),
    });
    setMenuOpenId(categoryId);
  };

  const openCreateModal = () => {
    setNameValue("");
    setModal({ mode: "create" });
  };

  const openRenameModal = (c: CaseCategory) => {
    setNameValue(c.name);
    setModal({ mode: "rename", id: c.id });
  };

  const closeModal = () => {
    setModal(null);
    setNameValue("");
  };

  const handleSubmit = async () => {
    const name = nameValue.trim();
    if (!name || !modal) return;
    if (modal.mode === "create") {
      await createCategory(name);
    } else {
      updateCategory(modal.id, { name });
    }
    closeModal();
  };

  const handleDrop = (categoryId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverId(null);
    const caseId = e.dataTransfer.getData(CASE_DRAG_MIME);
    if (caseId) addCaseToCategory(caseId, categoryId);
  };

  const allowDrop = (categoryId: string) => (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes(CASE_DRAG_MIME)) {
      e.preventDefault();
      setDragOverId(categoryId);
    }
  };

  const allowFavDrop = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes(CASE_DRAG_MIME)) {
      e.preventDefault();
      setFavDragOver(true);
    }
  };

  const handleFavDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setFavDragOver(false);
    const caseId = e.dataTransfer.getData(CASE_DRAG_MIME);
    if (caseId) toggleFavorite(caseId, true);
  };

  // Collapsed rail: Favorites + category initials, styled exactly like the
  // Cases/Trash/Forms nav buttons (MenuItem).
  if (isMenuCollapsed) {
    const favActive = activeFilter.type === "favorites" && onCasesPage;
    return (
      <div className="flex flex-col gap-y-1 mt-3 pt-3 border-t border-base-300">
        <Button
          title="Favorites"
          onClick={() => goToFilter({ type: "favorites", categoryId: null })}
          onDragOver={allowFavDrop}
          onDragLeave={() => setFavDragOver(false)}
          onDrop={handleFavDrop}
          className={cn(
            // justify-center to match the collapsed MenuItem rail above; the
            // icon is the only content, so left-aligning it looked misaligned.
            "btn border-0 w-full justify-center px-0 !min-h-10 shadow-none transition-colors",
            favActive
              ? "bg-blue-50 hover:bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:hover:bg-blue-900/40 dark:text-blue-300"
              : "hover:bg-base-300",
            // Text color hints droppable while dragging; fills when hovered.
            isDraggingCase && !favDragOver && "text-yellow-600 dark:text-yellow-400",
            favDragOver && "bg-yellow-100 dark:bg-yellow-950/40"
          )}
        >
          {favActive ? (
            <StarSolidIcon className="size-6 text-yellow-500" />
          ) : (
            <StarIcon className="size-6 text-base-content/60" />
          )}
        </Button>
        {categories.map((c) => {
          const isActive =
            activeFilter.type === "category" &&
            activeFilter.categoryId === c.id &&
            onCasesPage;
          return (
            <Button
              key={c.id}
              title={c.name}
              onClick={() => goToFilter({ type: "category", categoryId: c.id })}
              onDragOver={allowDrop(c.id)}
              onDragLeave={() => setDragOverId(null)}
              onDrop={handleDrop(c.id)}
              className={cn(
                "btn border-0 w-full justify-center px-0 !min-h-10 shadow-none transition-colors",
                isActive
                  ? "bg-blue-50 hover:bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:hover:bg-blue-900/40 dark:text-blue-300"
                  : "hover:bg-base-300 text-base-content/60",
                // Text color hints droppable while dragging; the hovered
                // target transitions to a background fill.
                isDraggingCase && dragOverId !== c.id && "text-blue-400",
                dragOverId === c.id && "bg-blue-100 dark:bg-blue-900/60"
              )}
            >
              <span className="flex items-center justify-center size-6 text-xs font-bold">
                {categoryInitials(c.name)}
              </span>
            </Button>
          );
        })}
      </div>
    );
  }

  const renderCategoryRow = (c: CaseCategory) => {
    const isActive =
      activeFilter.type === "category" &&
      activeFilter.categoryId === c.id &&
      onCasesPage;

    return (
      <div
        key={c.id}
        data-tour="category-row"
        onClick={() => goToFilter({ type: "category", categoryId: c.id })}
        onDragOver={allowDrop(c.id)}
        onDragLeave={() => setDragOverId(null)}
        onDrop={handleDrop(c.id)}
        className={cn(
          "group flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer text-sm transition-colors",
          isActive ? "bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300" : "hover:bg-base-300",
          // Text color hints droppable while dragging; the hovered target
          // transitions to a background fill.
          isDraggingCase && dragOverId !== c.id && "text-blue-400",
          dragOverId === c.id && "bg-blue-100 dark:bg-blue-900/60"
        )}
      >
        <HashtagIcon className="size-4 text-base-content/40 flex-shrink-0" />
        <span className="flex-1 min-w-0 truncate">{c.name}</span>
        {/* Count and "⋯" share one fixed box so they sit in the same spot —
            the count shows by default, the menu trigger on hover. */}
        <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
          {c.caseCount > 0 && (
            <span className="text-xs text-base-content/40 group-hover:hidden">
              {c.caseCount}
            </span>
          )}
          <button
            data-tour="category-menu-button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              if (menuOpenId === c.id) {
                setMenuOpenId(null);
              } else {
                openMenu(c.id, e.currentTarget);
              }
            }}
            className="hidden group-hover:flex items-center justify-center w-full h-full rounded hover:bg-gray-300 dark:hover:bg-base-content/10"
          >
            <EllipsisHorizontalIcon className="size-4 text-base-content/60" />
          </button>
        </div>
      </div>
    );
  };

  const menuCategory = categories.find((c) => c.id === menuOpenId);

  return (
    <div className="mt-4 pt-3 border-t border-base-300" data-tour="case-categories">
      <div className="flex items-center justify-between px-2 mb-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-base-content/40">
          Categories
        </span>
        <button
          title="New category"
          onClick={openCreateModal}
          className="p-0.5 rounded hover:bg-base-300"
          data-tour="new-category-button"
        >
          <PlusIcon className="size-4 text-base-content/60" />
        </button>
      </div>

      {/* Favorites pseudo-category */}
      <div
        data-tour="favorites-category"
        onClick={() => goToFilter({ type: "favorites", categoryId: null })}
        onDragOver={allowFavDrop}
        onDragLeave={() => setFavDragOver(false)}
        onDrop={handleFavDrop}
        className={cn(
          "flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer text-sm transition-colors",
          activeFilter.type === "favorites" && onCasesPage
            ? "bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300"
            : "hover:bg-base-300",
          // Text color hints droppable while dragging; fills when hovered.
          isDraggingCase && !favDragOver && "text-yellow-600 dark:text-yellow-400",
          favDragOver && "bg-yellow-100 dark:bg-yellow-950/40"
        )}
      >
        {activeFilter.type === "favorites" && onCasesPage ? (
          <StarSolidIcon className="size-4 text-yellow-500 flex-shrink-0" />
        ) : (
          <StarIcon className="size-4 text-base-content/60 flex-shrink-0" />
        )}
        <span className="flex-1 min-w-0 truncate">Favorites</span>
        {/* Same fixed box as the category rows so the count lines up. */}
        <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
          {favoritesCount > 0 && (
            <span className="text-xs text-base-content/40">{favoritesCount}</span>
          )}
        </div>
      </div>

      {/* User categories */}
      <div className="max-h-64 overflow-y-auto scrollbar-hide">
        {categories.map(renderCategoryRow)}
      </div>

      {categories.length === 0 && (
        <p className="px-2 py-1 text-xs text-base-content/40">
          Create a category to organize your cases.
        </p>
      )}

      {/* Per-category "⋯" menu — same DaisyUI menu styling/animation as
          Settings/Dropdown, but portaled to <body> so it isn't clipped by the
          scrollable category list. */}
      {menuCategory &&
        menuPos &&
        createPortal(
          <ul
            data-tour="category-menu"
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              top: menuPos.top,
              left: menuPos.left,
              width: MENU_WIDTH,
              transformOrigin: "top right",
              animation: "dropdownPop 0.15s ease-out",
            }}
            className="menu fixed z-[100] bg-base-100 rounded-lg p-2 shadow dark:border dark:border-base-300"
          >
            <li>
              <button
                onClick={() => {
                  openRenameModal(menuCategory);
                  setMenuOpenId(null);
                }}
                className="hover:bg-gray-100 dark:hover:bg-base-200"
              >
                <PencilSquareIcon className="size-4" />
                Rename
              </button>
            </li>
            <li>
              <button
                onClick={() => {
                  setDeletingCategory(menuCategory);
                  setMenuOpenId(null);
                }}
                className="hover:bg-red-50 dark:hover:bg-red-950/40 text-red-600 dark:text-red-400"
              >
                <TrashIcon className="size-4" />
                Delete
              </button>
            </li>
          </ul>,
          document.body
        )}

      {/* Category name modal (create + rename) — portaled to <body> so the
          transformed sidebar doesn't become its positioning container. */}
      {createPortal(
        <dialog
          ref={dialogRef}
          data-tour="category-dialog"
          className={`modal ${modal ? "modal-open" : ""}`}
        >
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg">
              {modal?.mode === "rename" ? "Rename Category" : "New Category"}
            </h3>
            <p className="text-sm text-base-content/60 mt-2 mb-4">
              Organize your cases into a category.
            </p>
            <Input
              title="Category name"
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSubmit();
              }}
              placeholder="e.g. Traffic Stops"
              className="w-full !max-w-full"
            />
            <div className="modal-action">
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!nameValue.trim()}
              >
                {modal?.mode === "rename" ? "Save" : "Create"}
              </Button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={closeModal}>close</button>
          </form>
        </dialog>,
        document.body
      )}

      {/* Delete confirmation modal — portaled to <body> like the name modal. */}
      {createPortal(
        <dialog
          data-tour="category-delete-dialog"
          className={`modal ${deletingCategory ? "modal-open" : ""}`}
        >
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg">Delete Category</h3>
            <p className="py-4 text-gray-600 dark:text-base-content/70">
              Delete the category "{deletingCategory?.name}"? Your cases stay —
              they're just removed from this category.
            </p>
            <div className="modal-action">
              <Button
                variant="outline"
                onClick={() => setDeletingCategory(null)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  if (deletingCategory) deleteCategory(deletingCategory.id);
                  setDeletingCategory(null);
                }}
              >
                Delete Category
              </Button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={() => setDeletingCategory(null)}>close</button>
          </form>
        </dialog>,
        document.body
      )}
    </div>
  );
}
